import {
  getLeoUuid,
  LeoUUID,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class ResetSpocPasswordRPC implements
  LeoRPC<ResetSpocPasswordRPC.Request, ResetSpocPasswordRPC.Response, ResetSpocPasswordRPC.Errors.Errors>
{
  abstract execute(
    request: ResetSpocPasswordRPC.Request
  ): Promise<LeoRPCResult<ResetSpocPasswordRPC.Response, ResetSpocPasswordRPC.Errors.Errors>>;
}

export namespace ResetSpocPasswordRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    spocId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isResetSpocPasswordRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly spocId: LeoUUID;
  
  
    constructor(
    projectId: LeoUUID, 
    spocId: LeoUUID
    ) {
      this.projectId = projectId;
      this.spocId = spocId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const spocId = getLeoUuid(dto, "spocId", InvalidRequestSpocIdError);
      return new Request(
        projectId,
        spocId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          spocId: this.spocId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `ResetSpocPasswordRPC(projectId='${this.projectId}',spocId='${this.spocId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `ResetSpocPasswordRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidSpocId = "INVALID_SPOC_ID",
    SendEmailFailed = "SEND_EMAIL_FAILED",
    ResetPasswordLimitReached = "RESET_PASSWORD_LIMIT_REACHED",
    EmailAlreadySent = "EMAIL_ALREADY_SENT"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `ResetSpocPasswordRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `ResetSpocPasswordRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidSpocIdDTO {
      code: string;
    }
    
    export class InvalidSpocId extends LeoRPCError<RPCError> {
      readonly isInvalidSpocId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSpocId;
        super(code);
      }
    
    
      toDTO(): InvalidSpocIdDTO {
        const dto: InvalidSpocIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSpocId.prototype.toString = function toString(): string {
      return `ResetSpocPasswordRPC.INVALID_SPOC_ID()`;
    };
    
    
    export interface SendEmailFailedDTO {
      code: string;
    }
    
    export class SendEmailFailed extends LeoRPCError<RPCError> {
      readonly isSendEmailFailed: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SendEmailFailed;
        super(code);
      }
    
    
      toDTO(): SendEmailFailedDTO {
        const dto: SendEmailFailedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SendEmailFailed.prototype.toString = function toString(): string {
      return `ResetSpocPasswordRPC.SEND_EMAIL_FAILED()`;
    };
    
    
    export interface ResetPasswordLimitReachedDTO {
      code: string;
    }
    
    export class ResetPasswordLimitReached extends LeoRPCError<RPCError> {
      readonly isResetPasswordLimitReached: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ResetPasswordLimitReached;
        super(code);
      }
    
    
      toDTO(): ResetPasswordLimitReachedDTO {
        const dto: ResetPasswordLimitReachedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ResetPasswordLimitReached.prototype.toString = function toString(): string {
      return `ResetSpocPasswordRPC.RESET_PASSWORD_LIMIT_REACHED()`;
    };
    
    
    export interface EmailAlreadySentDTO {
      code: string;
    }
    
    export class EmailAlreadySent extends LeoRPCError<RPCError> {
      readonly isEmailAlreadySent: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.EmailAlreadySent;
        super(code);
      }
    
    
      toDTO(): EmailAlreadySentDTO {
        const dto: EmailAlreadySentDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    EmailAlreadySent.prototype.toString = function toString(): string {
      return `ResetSpocPasswordRPC.EMAIL_ALREADY_SENT()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | InvalidSpocId | SendEmailFailed | ResetPasswordLimitReached | EmailAlreadySent;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | InvalidSpocIdDTO | SendEmailFailedDTO | ResetPasswordLimitReachedDTO | EmailAlreadySentDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidSpocId:
          return new InvalidSpocId();
        case RPCError.SendEmailFailed:
          return new SendEmailFailed();
        case RPCError.ResetPasswordLimitReached:
          return new ResetPasswordLimitReached();
        case RPCError.EmailAlreadySent:
          return new EmailAlreadySent();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends ResetSpocPasswordRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSpocIdError extends ResetSpocPasswordRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSpocIdError";
  }
}


export class InvalidResponseInvalidProjectIdError extends ResetSpocPasswordRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends ResetSpocPasswordRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidSpocIdError extends ResetSpocPasswordRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSpocIdError";
  }
}

export class InvalidResponseSendEmailFailedError extends ResetSpocPasswordRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSendEmailFailedError";
  }
}

export class InvalidResponseResetPasswordLimitReachedError extends ResetSpocPasswordRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseResetPasswordLimitReachedError";
  }
}

export class InvalidResponseEmailAlreadySentError extends ResetSpocPasswordRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseEmailAlreadySentError";
  }
}

